import { c as _c } from "react/compiler-runtime";
import { AccountStatus, ProgramType } from '@newfront-insurance/account-api';
import { useAccountSwitcherOptions, type Account } from '@newfront-insurance/admin-ui/accounts';
import { useCallback, useEffect, useState } from 'react';

/**
 * Nova search account result type that matches the expected structure
 * for the SearchRow component.
 */
export interface NovaSearchAccountResult {
  uuid: string;
  label: string;
  badges?: string[];
  description?: string;
}
export type SelectedAccount = NovaSearchAccountResult | null | undefined;
interface UseAccountSearchReturn {
  handleSearch: (value: string) => Promise<void>;
  handleReset: () => void;
  isLoading: boolean;
  results?: NovaSearchAccountResult[];
  error?: {
    statusText: string;
    data: {
      errorMessage: string;
    };
  };
}
interface UseAccountSearchProps {
  loggedInUserUuid: string;
  userScopes: string[];
}
const PROGRAM_TYPE_TO_BADGE: Record<ProgramType, string> = {
  [ProgramType.PROPERTY_AND_CASUALTY]: 'BI',
  [ProgramType.EMPLOYEE_BENEFITS]: 'TR'
};

/**
 * Converts an Account from admin-ui to NovaSearchAccountResult format.
 * Includes badges for program type and archived status.
 */
function accountToNovaSearchResult(account: Account): NovaSearchAccountResult {
  const badges: string[] = [];

  // Add archived badge if applicable
  if (account.status.status === AccountStatus.ARCHIVED) {
    badges.push('Archived');
  }

  // Add program type badge
  if (account.programType) {
    const programTypeBadge = PROGRAM_TYPE_TO_BADGE[account.programType];
    if (programTypeBadge) {
      badges.push(programTypeBadge);
    }
  }
  return {
    uuid: account.uuid,
    label: account.name,
    description: account.dba,
    badges: badges.length > 0 ? badges : undefined
  };
}

/**
 * Hook to fetch and search accounts for the Nova account selector.
 *
 * This hook uses the same data fetching logic as the admin-ui account switcher,
 * ensuring proper ACL (Access Control List) handling:
 * - Users with specific roles can search ALL accounts
 * - Other users can only see accounts they are related to via servicing team membership
 *
 * @param loggedInUserUuid - The UUID of the currently logged-in user
 * @param userScopes - The scopes/roles of the currently logged-in user
 * @returns Object containing search handlers, loading state, results, and error state
 */
export function useAccountSearch(t0) {
  const $ = _c(19);
  const {
    loggedInUserUuid,
    userScopes
  } = t0;
  const [searchValue, setSearchValue] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  let t1;
  if ($[0] !== loggedInUserUuid || $[1] !== userScopes) {
    t1 = {
      loggedInUserUuid,
      userScopes
    };
    $[0] = loggedInUserUuid;
    $[1] = userScopes;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    accounts,
    search,
    isLoading
  } = useAccountSwitcherOptions(t1);
  let t2;
  let t3;
  if ($[3] !== hasSearched || $[4] !== search || $[5] !== searchValue) {
    t2 = () => {
      if (hasSearched) {
        search(searchValue);
      }
    };
    t3 = [searchValue, search, hasSearched];
    $[3] = hasSearched;
    $[4] = search;
    $[5] = searchValue;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = async value => {
      setSearchValue(value);
      setHasSearched(true);
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleSearch = t4;
  let t5;
  if ($[9] !== search) {
    t5 = () => {
      setSearchValue("");
      setHasSearched(false);
      search("");
    };
    $[9] = search;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleReset = t5;
  let t6;
  if ($[11] !== accounts || $[12] !== hasSearched || $[13] !== searchValue) {
    t6 = hasSearched && searchValue.trim() ? accounts.map(accountToNovaSearchResult) : undefined;
    $[11] = accounts;
    $[12] = hasSearched;
    $[13] = searchValue;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const results = t6;
  let t7;
  if ($[15] !== handleReset || $[16] !== isLoading || $[17] !== results) {
    t7 = {
      handleSearch,
      handleReset,
      isLoading,
      results,
      error: undefined
    };
    $[15] = handleReset;
    $[16] = isLoading;
    $[17] = results;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}